import { s3BulkOperationChunkSize, S3_PRESIGNED_LINK_LIFESPAN_HOURS } from '@/config'
import { getFileNameFromPath } from '@/services/strings'
import { Directory, File, FileSystemItem } from '@/store/types/file'
import { S3 } from 'aws-sdk'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { chunkArray } from '@/services/arrays'
import { hoursToMinutes, hoursToSeconds } from './time'

function getS3SignedLinkLifeSpanSeconds(): number {
  const S3_PRESIGNED_LINK_LIFESPAN_SECONDS = hoursToSeconds(
    hoursToMinutes(S3_PRESIGNED_LINK_LIFESPAN_HOURS),
  )
  return S3_PRESIGNED_LINK_LIFESPAN_SECONDS
}

/**
 * Helper - let's prompt download programatically.
 * @param url which we simulate to click
 */
export function simulateDownloadLinkClick(url: string): void {
  const a = document.createElement('a')
  a.href = url
  a.target = '_parent'
  // Use a.download if available, it prevents plugins from opening.
  if ('download' in a) {
    const FILENAME = getFileNameFromPath(url)
    a.download = FILENAME
  }
  // Append the element for click to work.
  ;(document.body || document.documentElement).appendChild(a)
  if (!a.click) throw Error('The browser does not support .click() method')
  a.click()
  // Delete the element
  a.parentNode?.removeChild(a)
}

/**
 * Helper - let's have several download prompts
 * @param urls an array of urls
 */
export function simulateMultiLinkClick(urls: string[]) {
  function downloadNext(i: number) {
    if (i >= urls.length) {
      return
    }
    simulateDownloadLinkClick(urls[i])
    // Download the next file with a small timeout. The timeout is necessary
    // for IE, which will otherwise only download the first file.
    setTimeout(function () {
      downloadNext(i + 1)
    }, 500)
  }
  // Initiate the first download.
  downloadNext(0)
}

export const humanFileSize = (bytes: number) => {
  const thresh = 1024
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }
  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  let u = -1
  do {
    bytes /= thresh
    ++u
  } while (Math.abs(bytes) >= thresh && u < units.length - 1)
  return bytes.toFixed(1) + ' ' + units[u]
}

export const lastModifiedDisplay = (lastModifiedDate: Date): string => {
  // TODO add things like
  // today, 1 hour ago etc.
  if (!lastModifiedDate) {
    return ''
  }
  return new Date(lastModifiedDate).toISOString().slice(0, 19).replace('T', ' ')
}

export const sizeDisplay = (size: number): string => {
  if (!size) {
    return ''
  }
  return humanFileSize(size)
}

export const removePrefix = (prefix: string, key: string) => {
  if (key.startsWith(prefix)) {
    return key.substr(prefix.length)
  } else {
    return key
  }
}

export const getUuid = (pathMatch: string) => {
  const arr = pathMatch.split('/')
  return arr[0]
}

export const normalizedPath = (path: string) => {
  // Path always has this format
  // /           (root dir)
  // /dir1/dir2/filename.txt
  // /dir/dir2
  // no trailing slashes
  let normalized = path.replace(/\/\/+/g, '/')
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`
  }
  if (normalized.endsWith('/') && normalized.length > 1) {
    normalized = normalized.slice(0, normalized.length - 1)
  }
  return normalized
}

export const getPathFromLocation = (pathMatch: string) => {
  let newPathMatch = pathMatch
  // Remove trailing slash
  if (pathMatch.endsWith('/')) {
    newPathMatch = pathMatch.slice(0, pathMatch.length - 1)
  }
  const i = newPathMatch.indexOf('/')
  if (i === -1) {
    return '/'
  } else {
    return normalizedPath(pathMatch.slice(i))
  }
}

export const getPrefix = (path: string) => {
  // Assumes path was normalized with normalizedPath
  // Prefix always has this format
  // '' (root dir)
  // 'dir1/' (directory)
  // 'dir1/dir2/'
  const prefix = path.slice(1) // remove leading slash
  return !prefix ? '' : `${prefix}/`
}

export const getDirectoryKey = (path: string, name: string) => {
  // Assumes path was normalized with normalizedPath
  return `${getPrefix(path)}${name}/`
}

export const convertedFilesFromS3 = (prefix: string, files: Array<any>): Array<File> => {
  return files
    .filter((file) => removePrefix(prefix, file.Key)) // remove files that are marking directories
    .map((file: any) => {
      const name = removePrefix(prefix, file.Key)
      return new File(name, file.Key, file.Size, file.LastModified)
    })
}

export const convertedDirectoriesFromS3 = (prefix: string, directories: Array<any>) => {
  return directories
    .filter((dir) => removePrefix(prefix, dir.Prefix.replace(/\/*$/, ''))) // remove directories with empty name
    .map((dir: any) => {
      const name = removePrefix(prefix, dir.Prefix.replace(/\/*$/, ''))
      const s3Path = dir.Prefix
      return new Directory(name, s3Path)
    })
}

export class RenderroS3Client {
  s3: S3
  bucket: string

  constructor(s3Key: string, s3Secret: string, bucket: string, region: string) {
    const options = {
      apiVersion: '2006-03-01',
      accessKeyId: s3Key,
      secretAccessKey: s3Secret,
      region,
      signatureVersion: 'v4',
    }

    this.s3 = new S3(options)
    this.bucket = bucket
  }

  async loadDirectory(path: string) {
    const prefix = getPrefix(path)
    const data = await this.s3
      .listObjectsV2({
        Bucket: this.bucket,
        MaxKeys: s3BulkOperationChunkSize,
        Delimiter: '/',
        Prefix: prefix,
      })
      .promise()
    // TODO handle data.IsTruncated

    return {
      files: convertedFilesFromS3(prefix, data.Contents ? data.Contents : []),
      directories: convertedDirectoriesFromS3(
        prefix,
        data.CommonPrefixes ? data.CommonPrefixes : [],
      ),
    }
  }

  async createDirectory(path: string, name: string) {
    await this.s3
      .putObject({
        Bucket: this.bucket,
        Body: '',
        Key: getDirectoryKey(path, name),
      })
      .promise()
  }

  downloadSingle(s3Path: string): void {
    const url = this.getSignedS3Url(s3Path)
    simulateDownloadLinkClick(url)
  }

  downloadMultiple(s3PathsSet: Set<string>): void {
    const s3PathsArray = Array.from(s3PathsSet) // for convenient iteration without loosing `this` binding
    const urls = []
    for (const path of s3PathsArray) {
      urls.push(this.getSignedS3Url(path))
    }
    simulateMultiLinkClick(urls)
  }

  getSignedS3Url(s3Path: string): string {
    const url = this.s3.getSignedUrl('getObject', {
      Bucket: this.bucket,
      Key: s3Path,
      Expires: getS3SignedLinkLifeSpanSeconds(), // this takes seconds
    })
    return url
  }

  async listDirectoryKeys(dirS3Path: string): Promise<{ keys: Array<string>; truncated: boolean }> {
    // TODO get rid of truncated, make this function always return all keys
    const listParams = {
      Bucket: this.bucket,
      Prefix: dirS3Path,
    }
    const listedObjects = await this.s3.listObjectsV2(listParams).promise()
    if (!listedObjects) {
      return { keys: [], truncated: false }
    }
    if (!listedObjects.Contents || listedObjects.Contents.length === 0) {
      return { keys: [], truncated: false }
    }
    return {
      keys: listedObjects.Contents.map(({ Key }) => Key as string),
      truncated: listedObjects.IsTruncated || false,
    }
  }

  async emptyDirectory(dirS3Path: string) {
    // S3 doesn't really have a concept of directories
    // so "directory" should be in quotes in the above name.
    // This function is really removing all keys that are
    // matching dirS3Path* prefix.
    const { keys, truncated } = await this.listDirectoryKeys(dirS3Path)
    const deleteParams: any = {
      Bucket: this.bucket,
      Delete: {
        Objects: keys.map((Key) => ({ Key })),
        Quiet: true,
      },
    }
    await this.s3.deleteObjects(deleteParams).promise()

    if (truncated) {
      // Keep removing until it's completely empty
      // Only first 1000 items are being removed in a single
      // S3 API call above.
      await this.emptyDirectory(dirS3Path)
    }
  }

  async deletePaths(paths: Array<string>) {
    // TODO use this method above for emptyDirectory
    if (paths.length === 0) {
      return
    }
    const objects = paths.map((path) => ({
      Key: path,
    }))
    for (const chunk of chunkArray(objects, s3BulkOperationChunkSize)) {
      const params: any = {
        Bucket: this.bucket,
        Delete: {
          Objects: chunk,
          Quiet: true,
        },
      }
      await this.s3.deleteObjects(params).promise()
    }
  }

  async deleteItems(items: Array<FileSystemItem>) {
    for (const item of items) {
      if (item.isDirectory()) {
        await this.emptyDirectory(item.s3Path)
      }
    }
    await this.deletePaths(items.map((i) => i.s3Path))
  }

  async copyItem(item: FileSystemItem, destS3Path: string) {
    if (item.isFile()) {
      await this.s3
        .copyObject({
          Bucket: this.bucket,
          CopySource: `${this.bucket}/${item.s3Path}`,
          Key: destS3Path,
        })
        .promise()
    } else {
      // directory
      const { keys } = await this.listDirectoryKeys(item.s3Path)
      for (const key of keys) {
        await this.s3
          .copyObject({
            Bucket: this.bucket,
            CopySource: `${this.bucket}/${key}`,
            Key: `${destS3Path}/${removePrefix(item.s3Path, key)}`,
          })
          .promise()
      }
    }
  }

  async moveItem(item: FileSystemItem, destPath: string) {
    await this.copyItem(item, destPath)
    await this.deleteItems([item])
  }

  async downloadZipArchive(directories: Array<Directory>, files: Array<File>) {
    const zip = new JSZip()
    const allPaths: Array<string> = []
    for (const dir of directories) {
      const { keys, truncated } = await this.listDirectoryKeys(dir.s3Path)
      if (truncated) {
        // TODO
        alert('truncated! directory too large')
        return
      }
      allPaths.push(...keys)
    }

    allPaths.push(...files.map((file) => file.s3Path))

    for (const path of allPaths) {
      const obj = await this.s3.getObject({ Bucket: this.bucket, Key: path }).promise()
      const content = obj.Body as Uint8Array
      if (!content) {
        return
      }
      zip.file(path, content)
    }
    const blob = await zip.generateAsync({ type: 'blob' })
    // TODO give archive a better name
    // TODO give option to doownload individual files without zip
    saveAs(blob, 'archive.zip')
  }
}

/**
 * Helps to properly name new files and directories with regard to already existing files
 * To be used to resolve name conflicts in f.e. directory creation or item renaming.
 *
 * @param name suggested name
 * @param array existing names in directory
 * @returns a new name with an indication
 */
export function indicateNth(newName: string, nameArray?: string[]): string {
  if (!newName || typeof newName !== 'string')
    throw new Error('The name must be a non-empty string.')

  if (!nameArray || nameArray.length === 0) return newName
  const delimiter = '.'
  let currentHighestIndex = 0
  let indicatingName = newName

  const [filename, ...ext] = indicatingName.split('.')

  while (nameArray.includes(indicatingName)) {
    currentHighestIndex++
    const indication = '(' + currentHighestIndex + ')'
    indicatingName = `${filename + indication}` + `${ext.length ? '.' + ext.join(delimiter) : ''}`
  }
  return indicatingName
}
